import type { Article, Issue } from '@/constants';

import type { EventContent } from './envelope';

function toId(value: unknown): number | undefined {
	const id = Number.parseInt(`${value}`, 10);
	return Number.isInteger(id) && id > 0 ? id : undefined;
}

export interface ContentSubject {
	issue?: Pick<Issue, 'id' | 'name'> | null;
	article?: Pick<Article, 'id' | 'name' | 'IssueID'> | null;
	/** The language the content is read in. Also picks the article's title. */
	language?: string;
	contentType?: EventContent['content_type'];
	pageNumber?: number;
}

/**
 * The content context of an event, from the issue and article it is
 * **about**. Call sites pass what they know; `track()` decides whether that
 * is enough for the event. Ids are the GraphQL `id` of the issue and the
 * article; without an issue, the article's own `IssueID` (its parent) is
 * used, so an article shown on its own still names its issue.
 */
export function contentOf({
	issue,
	article,
	language,
	contentType,
	pageNumber,
}: ContentSubject): EventContent {
	const content: EventContent = {
		issue_id: toId(issue?.id) ?? toId(article?.IssueID),
		issue_title: issue?.name || undefined,
		content_language: language || undefined,
		content_type: contentType,
		article_id: toId(article?.id),
		article_title: (language && article?.name?.[language]) || undefined,
		page_number: pageNumber,
	};
	// A title is only sent with its id.
	if (content.issue_id === undefined) {
		delete content.issue_title;
	}
	if (content.article_id === undefined) {
		delete content.article_title;
	}
	return content;
}
